/**
 * Agents API Schemas
 *
 * Schemas for agent performance and coaching endpoints.
 * Matches the actual API response structure from:
 * - GET /api/v1/agents/me/performance
 * - GET /api/v1/agents/teams/leaderboard
 * - GET /api/v1/agents/:agentId/coaching/*
 */

import { z } from 'zod/v4';
import { dashboardCategoryDistributionItemSchema } from './dashboard';
import { lockedDemoOnlySchema, projectIdsFilterSchema } from './common';
import { callCategorySchema } from './records';
import {
  voiceMarkSchema,
  voiceThemeFilterCodeSchema,
  voiceThemeFilterEchoSchema,
} from './voice-of-customer';

// ============================================================================
// Query Schemas
// ============================================================================

/**
 * Which end of the team leaderboard to return. `asc` — the low end first
 * (bottom performers); `position` stays the rank from the top either way.
 * Without it the first `limit` rows by descending score never hold an agent
 * ranked below `limit` (#1094).
 */
export const teamLeaderboardOrderSchema = z.enum(['desc', 'asc']);

/**
 * How much of the leaderboard to return. `page` — `limit` rows from
 * `offset`; `team` — every scored agent, `limit`/`offset` ignored.
 *
 * Widgets that pick by TREND (needs attention, top improvers, the team trend)
 * must see the whole team: any score-ordered page drops the agents below it,
 * and paging from the client joined pages from different cache snapshots,
 * failed as a whole on one bad page and cost one full recomputation per page
 * — while the server already builds the whole list in memory (#1140).
 */
export const teamLeaderboardScopeSchema = z.enum(['page', 'team']);

/**
 * The agent screen's demo policy (`useDemoFilterFromAuth`), as the quality
 * block beside its numbers receives it — 'no' once the org has real records
 * (#1222). One field for every request of that screen.
 */
export const agentScreenIncludeDemoSchema = z
  .enum(['yes', 'no'])
  .optional()
  .default('yes');
export type AgentScreenIncludeDemo = z.infer<typeof agentScreenIncludeDemoSchema>;

/**
 * `GET /agents/teams/leaderboard` query — the SSOT the edge route validates
 * with (D18, #1338). A divergent copy used to sit here (`day`/`year`, a
 * `month` default, no `periodStart`/`periodEnd`) while the route validated
 * with its own: typed callers could send what the route refused. Moving the
 * route onto this schema changes nothing it accepts (design decision 7): every
 * field keeps the semantics of the route's former copy — `periodStart`/
 * `periodEnd` any string, `limit` not required to be whole.
 */
export const teamLeaderboardQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'quarter']).optional().default('week'),
  /** An exact window; overrides `period`. */
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  limit: z.coerce.number().min(1).max(100).optional().default(20),
  // An array index since #1140: `0.5` would read `entries[0.5]`.
  offset: z.coerce.number().int().min(0).optional().default(0),
  order: teamLeaderboardOrderSchema.optional().default('desc'),
  /** `team` — every scored agent in one response (#1140). */
  scope: teamLeaderboardScopeSchema.optional().default('page'),
  /** Same project filter the team metrics and distribution take — without it
   *  the bottom-performers card compared a project-scoped threshold with the
   *  whole organization's agents (#1094). */
  projectIds: projectIdsFilterSchema,
  /** The screen's demo policy, as for the agent card (#1355). */
  includeDemo: agentScreenIncludeDemoSchema,
});

export const agentIdParamSchema = z.object({
  agentId: z.string(),
});

export const coachingSessionIdParamSchema = z.object({
  sessionId: z.string(),
});

// ============================================================================
// Performance Response Schemas (matching actual API)
// ============================================================================

/**
 * Quality metric from A-G framework
 */
export const qualityMetricSchema = z.object({
  id: z.string(),
  name: z.string(),
  /**
   * Счёт по метрике. Читать ТОЛЬКО вместе со `scored`: у неоценённой метрики
   * здесь ноль-заполнитель, а не оценка.
   *
   * Поле намеренно осталось `number`, хотя честнее был бы `null`: клиент
   * валидирует весь ответ одной Zod-схемой и бросает на первой же несостыковке
   * (`BaseApiClient.validatedRequest`), поэтому `null` в уже открытой вкладке
   * со старым бандлом уронил бы не одну метрику, а весь дашборд — KPI, ранг,
   * график, рекомендации. Сузить тип до `number | null` можно будет релизом
   * позже, когда старых бандлов не останется.
   */
  score: z.number(),
  /**
   * Метрику оценивали. `false` — она отключена для канала (у чата нет
   * clarity/interruptions/key_moments), не применялась к этим разговорам или у
   * агента нет записей за период. Это НЕ то же, что счёт 0: ноль ставит сама
   * рубрика за проваленный критерий, и он попадает в зоны роста и коучинг.
   *
   * ОБЯЗАТЕЛЬНОЕ, в отличие от одноимённого поля в `agentRecordSchema`, и это
   * не недосмотр. Умолчание `true` здесь стояло ровно один круг ревью и было
   * снято: сборка edge-api старше `edd36ccfe` слала неизмеренной метрике
   * `score: 0` и никакого флага, поэтому умолчание превращало этот ноль в
   * настоящую оценку — то есть воскрешало #467, ради которого поле и
   * заведено. Отсутствие флага у метрики — не «прежнее поведение», а потеря
   * единственного признака, отличающего провал от неизмеренного.
   *
   * У записи асимметрия обратная: ноль-заполнитель в `qualityScore` появился
   * вместе с флагом, в ЭТОМ PR. До него сервер слал туда настоящий балл, и
   * умолчание `true` действительно воспроизводит прежнее поведение.
   *
   * Побочно: обязательность — единственное, чем контрактный тест ловит
   * `buildQualityMetrics`, переставший отдавать флаг. Умолчание молча
   * подставляло бы `true` и делало этот тест слепым.
   */
  scored: z.boolean(),
  maxScore: z.number(),
  isStrength: z.boolean(),
  isFocusArea: z.boolean(),
});

/**
 * Focus area for improvement
 */
export const focusAreaSchema = z.object({
  metricId: z.string(),
  metricName: z.string(),
  currentScore: z.number(),
  targetScore: z.number(),
  /** Stable generateTips() keys (WS6-T2), e.g. 'warmPersonalizedGreeting' —
   *  NOT English sentences. The frontend localizes each one via
   *  `agentDashboard.tips.<key>`. */
  tips: z.array(z.string()),
  exampleCallIds: z.array(z.string()),
});

/**
 * Recent record/call for agent
 */
export const agentRecordSchema = z.object({
  id: z.string(),
  type: z.enum(['call', 'chat']),
  /**
   * Остаётся `number` НАМЕРЕННО. Клиент валидирует весь ответ одной схемой и
   * бросает на первой несостыковке (`BaseApiClient.validatedRequest`), а
   * edge-api выкатывается раньше ui-worker и вкладка живёт со старым бандлом
   * сколько угодно: `null` уронил бы не одну строку, а весь дашборд агента.
   * Ровно это откатил `edd36ccfe` для `qualityMetricSchema.score` — и там же
   * задан образец: правду о состоянии несёт соседний флаг.
   *
   * При `scored: false` здесь ноль-заполнитель. Сузить тип до
   * `number | null` можно будет релизом позже, когда старых бандлов не
   * останется.
   */
  qualityScore: z.number(),
  /**
   * Балл известен. `false` — записи нечего показывать: анализа ещё нет, он не
   * удался, либо рубрика к разговору не применялась (звонок категории «other»
   * хранит `metrics = {}` по устройству). Это НЕ ноль: ноль ставит сама
   * рубрика за проваленный разговор (#607).
   *
   * `.default(true)` — не украшение. Схема эта же валидирует ответ на
   * КЛИЕНТЕ, и обязательное поле уронило бы весь дашборд на ответе от сборки
   * edge-api, которая его ещё не отдаёт: откат одного воркера, staging, где
   * `detect-changes` пропустил деплой edge-api. Умолчание `true` = прежнее
   * поведение (показать число).
   *
   * Окно терпимости названо точно: она покрывает откат до любой сборки начиная
   * с `edd36ccfe` (25.08) — то есть до текущего main и всего, что между. Глубже
   * не покрывает: там нет и `metrics[].scored`, который остаётся обязательным
   * НАМЕРЕННО (см. соседнее поле — умолчание там воскрешает #467). Это
   * сознательный размен: молчаливая ложь про метрику хуже, чем громкий отказ
   * разобрать ответ пятидневной давности.
   */
  scored: z.boolean().optional().default(true),
  date: z.string(),
  duration: z.number().int(),
  contactName: z.string(),
});

/**
 * Quality score breakdown
 */
export const qualityScoreSchema = z.object({
  /**
   * Средний балл за период. Читать ТОЛЬКО вместе со `scored`: при
   * `scored: false` здесь ноль-заполнитель, а не результат.
   *
   * Остаётся `number` по той же причине, что и `agentRecordSchema.qualityScore`
   * выше: клиент валидирует весь ответ одной схемой и бросает на первой
   * несостыковке, а edge-api выкатывается раньше ui-worker — `null` уронил бы
   * не карточку, а весь дашборд агента во всякой открытой вкладке со старым
   * бандлом. Сузить до `number | null` можно релизом позже.
   */
  current: z.number(),
  maxScore: z.number(),
  trend: z.enum(['up', 'down', 'stable']),
  trendValue: z.number(),
  trendPeriod: z.string(),
  /**
   * Отклонение от среднего по команде, в процентах. Тоже читать вместе со
   * `scored`: без собственного балла сравнивать не с чем, и ноль здесь значил
   * бы «вы ровно на уровне коллег» — утверждение, которого никто не измерял.
   */
  vsTeamAvg: z.number(),
  /**
   * За период есть хотя бы одна оценённая запись.
   *
   * `false` — рубрика не оценила ничего: например, все звонки попали в
   * категорию «прочее», которая хранит `metrics = {}` по устройству. Это НЕ
   * ноль: ноль ставит сама рубрика за проваленный разговор (#607). Пока флага
   * не было, страница показывала «My Score 0/100» над строками с прочерками —
   * и противоречила отгруженной документации, где прочерк объявлен
   * отсутствием оценки, а не нулём (#610).
   *
   * `.default(true)` — не украшение, а то же окно терпимости, что у
   * `agentRecordSchema.scored`: схема валидирует ответ на КЛИЕНТЕ, и
   * обязательное поле уронило бы дашборд на ответе от сборки edge-api, которая
   * его ещё не отдаёт (откат воркера; staging, где `detect-changes` пропустил
   * деплой edge-api). Умолчание `true` = прежнее поведение, показать число.
   */
  scored: z.boolean().optional().default(true),
  /**
   * How many records of the period carry a score — the n `current` averages
   * over (D17, #1338 PR C). Optional: an answer from an edge without it still
   * parses.
   */
  scoredCount: z.number().int().nonnegative().optional(),
});

/**
 * Ranking information
 */
export const rankingSchema = z.object({
  position: z.number().int(),
  total: z.number().int(),
  percentile: z.number(),
});

/**
 * Daily score data point for quality trend chart
 */
export const scoreDaySchema = z.object({
  date: z.string(), // ISO date "2026-02-18"
  score: z.number(), // 0-100
});

/**
 * Call statistics for a period
 */
export const callStatsSchema = z.object({
  totalCalls: z.number().int(),
  avgDuration: z.number().int(), // in seconds
});

/**
 * One week's average quality score on the full-horizon (26-week) series used
 * for "Longest Streak" / "Personal bests" on My Progress — independent of the
 * dashboard's selected period (WS4-T8). `weekStart` is the date of the first
 * day of the week, cut in the organization's own zone (`settings_json.timezone`,
 * UTC when unset) on its own first day of the week — `settings_json.weekStartsOn`,
 * Monday when unset (#864; `weekStartDay` in @aica/core). Weeks with zero analyses are omitted from the
 * array entirely rather than sent as 0 — absence of data must never be
 * indistinguishable from a real bad week.
 */
export const weeklyScoreSchema = z.object({
  weekStart: z.string(), // ISO date "2026-06-29"
  avgScore: z.number(), // 0-100, normalized
});

/**
 * Agent performance response - matches GET /api/v1/agents/me/performance
 * and GET /api/v1/agents/:agentId/performance
 */
export const agentPerformanceResponseSchema = z.object({
  qualityScore: qualityScoreSchema,
  ranking: rankingSchema,
  metrics: z.array(qualityMetricSchema),
  focusArea: focusAreaSchema.nullable(),
  recentRecords: z.array(agentRecordSchema),
  /** Agent name — populated when viewing another agent's performance */
  agentName: z.string().optional(),
  /**
   * Установка CRM, чьи записи показаны, — у сотрудника без аккаунта Aelo, чьи
   * записи сужены до установки (#1927); нет поля — страница по всем записям
   * `agentId` (аккаунт, одна установка, без установки). Клиент передаёт её
   * соседним запросам страницы. `.optional()`: edge-api деплоится раньше
   * ui-worker.
   */
  installationId: z.string().nullable().optional(),
  /**
   * Средний балл команды за то же окно — канонический, то есть посчитанный
   * так же, как балл самого агента: балл каждой записи, потом среднее.
   *
   * Отдаётся потому, что клиент считал его сам — средним по шести командным
   * средним из `teamCategoryAverages`. Это average-then-scale, ровно то
   * правило, которое #603 устраняет: на окне, где рядом лежат 8 и 85, линия
   * агента на графике (канонический балл) и линия команды (среднее по
   * столбцам) расходились на десятки пунктов, и агент сравнивался с числом,
   * посчитанным не по его правилу. Тот же экран при этом печатал `vsTeamAvg`,
   * посчитанный на сервере канонически, — две разные команды на одной
   * странице.
   *
   * `.optional()`, а не обязательное: поле новое, и старая сборка edge-api его
   * не отдаёт (`edd36ccfe`). `null` — команда за окно без единой оценки.
   */
  teamAvgScore: z.number().nullable().optional(),
  /** Daily quality scores for the selected period */
  scoreHistory: z.array(scoreDaySchema).optional(),
  /** Aggregated call statistics for the period */
  callStats: callStatsSchema.optional(),
  /** Records count per day for the period (agent's own records only) */
  recordsPerDay: z
    .array(z.object({ day: z.string(), records: z.number().int() }))
    .optional(),
  /** Previous period stats for month-over-month comparison */
  prevPeriod: z
    .object({
      avgScore: z.number().nullable(),
      totalCalls: z.number().int(),
      /** Of them, the scored ones — the n `avgScore` averages over (#1338 PR C). */
      scoredCount: z.number().int().nonnegative().optional(),
    })
    .optional(),
  /** Real per-category average across the whole team for the same window (0-100) */
  teamCategoryAverages: z.record(z.string(), z.number()).optional(),
  /** Full-horizon (26-week) weekly averages for Personal Bests/Longest Streak
   *  — independent of the selected period (WS4-T8) */
  allTimeWeeklyScores: z.array(weeklyScoreSchema).optional(),
  /** Best single-call score across the same full horizon (0-100). Null when
   *  there are no analyses in the window — never 0 (WS4-T8). The "Best Score"
   *  card is labeled "all time", so this must not be period-scoped either. */
  allTimeBestScore: z.number().nullable().optional(),
  /** Call-category mix (count + avg quality score per category) scoped to
   *  this agent's own records for the selected period (WS4-T10) — same shape
   *  as the dashboard's org-wide categoryDistribution. */
  categoryDistribution: z.array(dashboardCategoryDistributionItemSchema).optional(),
  /**
   * The place among the people rated in the window (#1338 PR C): only those
   * with at least `TEAM_MIN_SCORED` scored conversations are ranked — the
   * Team tab's «few ratings» rule, so the two screens rank one team one way.
   * `position` is `null` while the person itself has fewer; `scoredCount` is
   * its own n in the team reading. The team is never narrowed by a project
   * filter. `ranking` above keeps its rule for the role dashboards.
   */
  ratedRanking: z
    .object({
      position: z.number().int().positive().nullable(),
      total: z.number().int().nonnegative(),
      scoredCount: z.number().int().nonnegative(),
    })
    .optional(),
  /**
   * The projects the own reads were narrowed to, sorted — present only when a
   * filter was asked and applied (`/me/performance?projectIds=`). Its absence
   * under a chosen filter means an edge that ignored it (#1338 PR C).
   */
  projectIds: z.array(z.string()).optional(),
  /** When the body was computed — a cached body keeps its own time. */
  asOf: z.string().datetime().optional(),
});

// ============================================================================
// Leaderboard Response Schemas (matching actual API)
// ============================================================================

/**
 * Leaderboard entry - matches API response structure
 */
export const leaderboardEntrySchema = z.object({
  /** An Aelo account's id, else the CRM id as recorded (one row per person, #1927). */
  agentId: z.string(),
  /**
   * The installation of a CRM employee without an Aelo account — with `agentId` the
   * row's identity (`7` from two portals is two rows). Optional and nullable: an
   * answer from a rolled-back or cached edge without it still parses.
   */
  installationId: z.string().nullable().optional(),
  agentName: z.string(),
  agentEmail: z.string(),
  qualityScore: z.number(),
  /** Records with a current analysis that carries metrics, scored or not. */
  recordCount: z.number().int(),
  /**
   * Of them, the ones with a score — the n `qualityScore` averages over (D18).
   * The handler always sets it; optional so an answer from a rolled-back or
   * cached edge without it still parses.
   */
  scoredCount: z.number().int().nonnegative().optional(),
  trend: z.enum(['up', 'down', 'stable', 'new']),
  /** null when there is no previous-period score to compare against (trend: 'new') */
  trendValue: z.number().nullable(),
  position: z.number().int(),
});

/**
 * Team leaderboard response - matches GET /api/v1/agents/teams/leaderboard
 */
export const teamLeaderboardResponseSchema = z.object({
  entries: z.array(leaderboardEntrySchema),
  teamAvgScore: z.number(),
  period: z.string(),
  total: z.number().int(),
});

// ============================================================================
// Coaching Schemas
// ============================================================================

export const coachingTipSchema = z.object({
  id: z.string(),
  category: z.string(),
  title: z.string(),
  content: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
});

export const coachingSessionSchema = z.object({
  id: z.string(),
  agentId: z.string(),
  recordId: z.string().nullable(),
  status: z.enum(['pending', 'in_progress', 'completed']),
  tips: z.array(coachingTipSchema),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});

export const getCoachingTipsResponseSchema = z.object({
  tips: z.array(coachingTipSchema),
});

export const getCoachingSessionsResponseSchema = z.object({
  sessions: z.array(coachingSessionSchema),
  total: z.number().int(),
});

export const createCoachingSessionRequestSchema = z
  .object({
    recordId: z.string().optional(),
    focusAreas: z.array(z.string()).optional(),
  })
  .strict();

export const createCoachingSessionResponseSchema = z.object({
  session: coachingSessionSchema,
});

export const completeCoachingSessionRequestSchema = z
  .object({
    feedback: z.string().optional(),
    rating: z.number().int().min(1).max(5).optional(),
  })
  .strict();

// ============================================================================
// Additional Coaching Schemas
// ============================================================================

// SSOT for the coaching-insights query contract. The edge route re-exports this
// (see apps/edge-api/src/routes/agents/coaching-schemas.ts) so the shared schema
// and the endpoint can't drift. `today`/`quarter` and the explicit custom window
// (periodStart/periodEnd) mirror what AgentDashboard actually sends.
export const coachingInsightsQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'quarter']).optional().default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  includeDemo: agentScreenIncludeDemoSchema,
});

/**
 * Установка CRM, под которой открыта страница сотрудника без аккаунта Aelo
 * (#1927): один и тот же `agentId` в двух порталах — два человека. Id установки
 * — свободный текст, поэтому только длина. Параметр у маршрутов
 * `/agents/:agentId/*` — у `/me/*` его нет: «я» определён аккаунтом.
 */
export const agentInstallationParamSchema = z.string().min(1).max(200);

/** `GET /agents/:agentId/coaching/insights` query: the shared one plus the installation. */
export const coachingInsightsByIdQuerySchema = coachingInsightsQuerySchema.extend({
  installation: agentInstallationParamSchema.optional(),
});

/**
 * `GET /agents/me|:agentId/performance` query — the SSOT the edge route
 * validates with. (A divergent copy used to sit at the top of this file with
 * periods and a project filter the route never accepted.)
 */
export const agentPerformanceQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'quarter']).optional().default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  includeDemo: agentScreenIncludeDemoSchema,
});

/**
 * `GET /agents/:agentId/performance` query: the shared one plus the installation
 * of an employee without an Aelo account (#1927). Its own schema: `/me/*` has no
 * such parameter, and a parameter a route accepts and ignores would be a silent
 * lie.
 */
export const agentByIdPerformanceQuerySchema = agentPerformanceQuerySchema.extend({
  installation: agentInstallationParamSchema.optional(),
});

/**
 * `GET /agents/me/performance` query: the shared one plus the project filter
 * of the unified dashboard's personal view (#1338 PR C) — every read of the
 * route, the team's included, is cut to the chosen projects. Its own schema:
 * `/:agentId/performance` takes no project filter, and a parameter a route
 * accepts and ignores would be a silent lie.
 */
export const agentMePerformanceQuerySchema = agentPerformanceQuerySchema.extend({
  projectIds: projectIdsFilterSchema,
});

/** `GET /agents/me/coaching/weekly` query. */
export const weeklyCoachingPlanQuerySchema = z.object({
  includeDemo: agentScreenIncludeDemoSchema,
});

/**
 * `GET /agents/coaching/stats` (#2294): the dashboard's demo policy — 'no'
 * leaves the demo's coaching out, as the other cards leave out its calls.
 */
export const coachingStatsQuerySchema = z.object({
  includeDemo: agentScreenIncludeDemoSchema,
});

export const supervisorNotesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});

export const createSupervisorNoteBodySchema = z.object({
  content: z.string().min(1).max(5000),
  recordId: z.string().optional(),
  isPrivate: z.boolean().default(false),
});

/**
 * Supervisor note, matching the actual shape returned by the coaching notes
 * routes (apps/edge-api/src/routes/agents/coaching.ts): agentId/agentName
 * identify the note's recipient, supervisorId/supervisorName its author.
 * recordId is omitted from the response entirely when the note isn't linked
 * to a call (not sent as null).
 */
export const supervisorNoteSchema = z.object({
  id: z.string(),
  agentId: z.string(),
  agentName: z.string().nullable(),
  supervisorId: z.string(),
  supervisorName: z.string().nullable(),
  recordId: z.string().optional(),
  /**
   * The supervisor's text about the employee's calls. Absent for a narrowed
   * access grant without `evidence.text` (E1.5, #1336): who wrote to whom and
   * when stays.
   */
  content: z.string().optional(),
  createdAt: z.string(),
});

/**
 * A single A-G metric id (aggregation.ts METRIC_NAMES, e.g. 'greetingScore')
 * paired with its normalized 0-100 score — NOT a baked English sentence.
 * Shared shape for strengths/areasToImprove/recognition (WS6-T2b). The
 * frontend localizes the metric id via `localizeMetricLabel`/`categoryNames.*`
 * (WS6-T1/T2) and renders the sentence via `agentDashboard.insights.*`
 * templates.
 */
export const coachingMetricScoreSchema = z.object({
  metricId: z.string(),
  score: z.number(),
});

/**
 * One item of the agent's live coaching plan (WS7-T5): a coaching criterion
 * aggregated from the LLM's per-call `improvements[]`, replacing the static
 * generateTips() templates with what the agent actually did.
 *
 * `criterionKey` is a stable key the frontend localizes; `advice`/`actionItems`/
 * `exampleQuote` are LLM-authored content in the analysis language, shown as-is
 * (same convention as call summaries). Every quantified field is nullable — the
 * LLM fills them unevenly across prompt profiles, so the card degrades
 * field-by-field rather than hiding the whole item.
 */
export const coachingImprovementItemSchema = z.object({
  criterionKey: z.string(),
  /** How many of the agent's calls in the window raised this criterion. */
  frequency: z.number().int().positive(),
  advice: z.string().nullable(),
  actionItems: z.array(z.string()),
  exampleQuote: z.string().nullable(),
  /** Record id of the freshest call carrying the example quote (drill-down). */
  exampleRecordId: z.string().nullable(),
  /** Mean predicted score gain across the calls that predicted one. */
  scoreDeltaPredicted: z.number().nullable(),
  benchmarkPercentile: z.number().min(0).max(100).nullable(),
  timeToMasteryDays: z.number().nullable(),
  priority: z.enum(['low', 'medium', 'high']).nullable(),
});

export const aiCoachingInsightsSchema = z.object({
  strengths: z.array(coachingMetricScoreSchema),
  areasToImprove: z.array(coachingMetricScoreSchema),
  /** Stable generateTips() keys (WS6-T2) — NOT English sentences. The
   *  frontend localizes each one via `agentDashboard.tips.<key>`. */
  actionItems: z.array(z.string()),
  /** The live coaching plan from the LLM's per-call improvements (WS7-T5).
   *  Optional + empty when no analyzed call in the window carried improvements —
   *  the UI then falls back to the generateTips-based actionItems above. */
  coachingItems: z.array(coachingImprovementItemSchema).optional(),
  // overallAssessment remains a full English sentence baked server-side
  // (apps/edge-api/src/routes/agents/coaching.ts buildInsightsResponse) —
  // a known, separate WS6 debt, not fixed by WS6-T2b. No UI component renders
  // it: show it only after it is localized, never as-is to ru/uk/es users.
  overallAssessment: z.string(),
});

/**
 * Weekly coaching plan — must match the backend response shape produced by
 * `generateWeeklyPlan()` in apps/edge-api/src/routes/agents/coaching.ts.
 */
export const weeklyCoachingPlanSchema = z.object({
  generatedAt: z.string(),
  /** ISO instant: 00:00 UTC of the organization's first day of the current week (#864). */
  weekStart: z.string(),
  primaryFocus: z.object({
    metricId: z.string(),
    // Still the raw English display name (aggregation.ts METRIC_NAMES,
    // unchanged by WS6-T2/T2b). Localize from `metricId` in the UI
    // (localizeMetricLabel); this name is only the fallback for an unknown id.
    // The card is shown to every organization whose plan has `enableCoaching`
    // (on by default in every plan, product-features.ts coaching.workspace).
    metricName: z.string(),
    currentScore: z.number(),
    targetScore: z.number(),
  }),
  /** Stable generateTips() keys (WS6-T2) — NOT English sentences. */
  actionItems: z.array(z.string()),
  /** Structured metricId+score (WS6-T2b) — NOT baked English sentences. The
   *  frontend localizes via `agentDashboard.insights.recognition`. */
  recognition: z.array(coachingMetricScoreSchema),
  /** Structured metricId (WS6-T2b) — always the same metric as primaryFocus,
   *  so no score is carried. The frontend localizes via
   *  `agentDashboard.insights.motivation`. */
  motivation: z.object({ metricId: z.string() }),
  resources: z.array(z.string()),
});

export const coachingInsightsResponseSchema = z.object({
  insights: aiCoachingInsightsSchema,
});

export const weeklyCoachingPlanResponseSchema = z.object({
  // null when the plan can't be generated (insufficient data or generation failed).
  plan: weeklyCoachingPlanSchema.nullable(),
  status: z.enum(['available', 'insufficient_data', 'not_generated']),
  minRecordsRequired: z.number().int().optional(),
  currentRecordCount: z.number().int().optional(),
});

export const supervisorNotesResponseSchema = z.object({
  notes: z.array(supervisorNoteSchema),
  total: z.number().int(),
});

/**
 * «Мои встречи коучинга» (#2053): встречи, где сотрудник — тот, кого коучат,
 * за окно `[start, end)` — начало входит, конец нет. Окно обязательно и
 * явное: относительный период пересчитывал бы «сейчас» на каждой странице.
 */
export const MY_COACHING_SESSIONS_MAX_LIMIT = 50;

export const myCoachingSessionsQuerySchema = z
  .object({
    start: z.string().datetime({ offset: true }),
    end: z.string().datetime({ offset: true }),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(MY_COACHING_SESSIONS_MAX_LIMIT)
      .optional()
      .default(5),
    offset: z.coerce.number().int().min(0).optional().default(0),
  })
  .refine((query) => Date.parse(query.start) < Date.parse(query.end), {
    message: 'start must be before end',
    path: ['end'],
  });

export const myCoachingSessionSchema = z.object({
  id: z.string(),
  scheduledAt: z.string(),
  status: z.enum(['scheduled', 'completed', 'cancelled']),
  supervisorName: z.string().nullable(),
  /** Повестка/итог встречи — поле `notes` встречи. */
  notes: z.string().optional(),
});

export const myCoachingSessionsResponseSchema = z.object({
  /** Окно, как его применил edge: `[start, end)`. */
  start: z.string(),
  end: z.string(),
  sessions: z.array(myCoachingSessionSchema),
  /** n — встреч во всём окне, а не на странице. */
  total: z.number().int().nonnegative(),
});

export const supervisorNoteResponseSchema = z.object({
  note: supervisorNoteSchema,
});

// ============================================================================
// Competition Schemas
// ============================================================================

export const meCompetitionQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'quarter']).optional().default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().min(1).max(20).optional().default(5),
});

export const competitionEntrySchema = z.object({
  position: z.number().int(),
  agentName: z.string(),
  qualityScore: z.number(),
  isMe: z.boolean(),
});

export const meCompetitionResponseSchema = z.object({
  entries: z.array(competitionEntrySchema),
  myPosition: z.number().int().nullable(),
  myScore: z.number().nullable(),
  period: z.string(),
  total: z.number().int(),
});

// ============================================================================
// Script Adherence Schemas
// ============================================================================

export const scriptAdherenceQuerySchema = z.object({
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  /** @deprecated legacy single-project filter — kept for one release cycle
   *  (SPA and edge-api deploy separately) so a stale bundle still filters.
   *  Ignored whenever `projectIds` is present and non-empty. */
  projectId: z.string().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
  // Mirror the dashboard's demo scope so the Quality-tab widget doesn't blend
  // demo calls into adherence averages while the surrounding dashboard is
  // scoped to real records only ('no' = exclude demo). Absent = include demo,
  // as before the script-steps report (#879) — unlike that report, whose own
  // default is 'no'.
  includeDemo: z.enum(['yes', 'no']).optional(),
});

/**
 * Per-agent script adherence average — only agents with at least one scored
 * call in the window are included (absence of data must never render as 0,
 * see supervisorDashboard.quality.scriptAdherence widget).
 */
export const agentScriptAdherenceSchema = z.object({
  agentId: z.string(),
  /**
   * The CRM installation of an employee with no Aelo account (the id is local to
   * the portal), else `null` (#1927). A row is a person, not an `agentId`: one
   * unlinked id in two portals is two rows told apart by this field. Optional so
   * the page survives an edge-api rolled back to the release without it.
   */
  installationId: z.string().nullable().optional(),
  agentName: z.string().nullable(),
  avgAdherence: z.number(),
  callCount: z.number().int().positive(),
});

/**
 * One script step aggregated across the window (WS7-T6): how often it was
 * evaluated (`callCount`) and how often the agent skipped it (`skipCount`,
 * i.e. `isCompleted=false`). Only steps skipped at least once are returned,
 * ordered by absolute skip volume. `skipRate` is skipCount/callCount (0-1).
 */
export const skippedScriptStepSchema = z.object({
  stepId: z.string(),
  stepName: z.string(),
  skipCount: z.number().int().positive(),
  callCount: z.number().int().positive(),
  skipRate: z.number().min(0).max(1),
});

export const scriptAdherenceResponseSchema = z.object({
  agents: z.array(agentScriptAdherenceSchema),
  orgAvgAdherence: z.number().nullable(),
  totalCallsWithScript: z.number().int().nonnegative(),
  /** Top skipped steps org-wide over the window; empty when no step was ever
   *  skipped (or no scored calls). See skippedScriptStepSchema. */
  skippedSteps: z.array(skippedScriptStepSchema),
});

// ============================================================================
// Team Objections Schemas (WS7-T7)
// ============================================================================

export const objectionsQuerySchema = z.object({
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  /** @deprecated legacy single-project filter — see scriptAdherenceQuerySchema's note. */
  projectId: z.string().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
  /**
   * The demo policy (`useDemoFilterFromAuth`) of the screen that asks - the Quality
   * card on the dashboards and the Objections screen alike, so both count the
   * same calls: `'no'` - real records only, `'yes'` - demo ones too. Absent -
   * demo records are counted: what a client older than this field asked, so an
   * edge-api rollback (the schema is not strict, an unknown key is dropped) and
   * a stale bundle both keep their numbers.
   */
  includeDemo: z.enum(['yes', 'no']).optional(),
});

/**
 * One objection CODE the team fails to handle (WS7-T7, codes - #842): in how
 * many calls it arose (`encounterCount`) and in how many at least one
 * objection of it was left unhandled (`unhandledCount`). The unit is the call.
 * `unhandledRate` is unhandledCount/encounterCount (0-1).
 *
 * `code` is a plain string, not an enum: a UI older than a future code set
 * must degrade to showing a wording, not reject the whole response. The UI
 * localizes the name by code; the server sends no display name.
 */
export const teamObjectionSchema = z.object({
  code: z.string(),
  /**
   * @deprecated #842 - the most frequent live wording of the code. Optional
   * for clients from this release on; the server still sends it, because the
   * v2.43.0 client requires it and edge-api ships before ui-worker. Removal
   * from the response - the release after this one (#984).
   */
  type: z.string().optional(),
  /** Nonnegative: a code the team always handles is a valid list item. */
  unhandledCount: z.number().int().nonnegative(),
  encounterCount: z.number().int().positive(),
  unhandledRate: z.number().min(0).max(1),
  /** @deprecated #842 - `sampleQuotes[0]`; optional and still sent, same reason as `type` (#984). */
  sampleQuote: z.string().nullable().optional(),
  /** The freshest quote of an UNHANDLED objection of the code (at most one - what the card shows). */
  sampleQuotes: z.array(z.string()),
  /** Up to 3 live wordings of the code, most calls first. */
  wordings: z.array(z.string()),
});

export const objectionsResponseSchema = z.object({
  objections: z.array(teamObjectionSchema),
  /** Calls in the window that carried at least one objection (the denominator
   *  context for the card); 0 -> honest empty-state, never a fabricated row. */
  totalCallsWithObjections: z.number().int().nonnegative(),
  /** Share of (call, reason) pairs not recognized - each unrecognized wording is its own reason (0-1). */
  unmappedShare: z.number().min(0).max(1),
  /** Share of (call, reason) pairs coded `other` - no reason of the set fits (0-1). */
  otherShare: z.number().min(0).max(1),
  /** The window held more calls than the scan cap; counts are a lower bound. */
  truncated: z.boolean(),
});

// ============================================================================
// Objection Stream Schemas (#846) - the "Objections" screen, market and team views
// ============================================================================

/** The widest window the stream scans: a calendar quarter plus a DST hour (see reports.ts). */
export const OBJECTION_STREAM_MAX_WINDOW_DAYS = 93;

export const objectionStreamQuerySchema = objectionsQuerySchema.extend({
  /** Only calls of this category; absent - every category. */
  callCategory: callCategorySchema.optional(),
  /**
   * Only calls of this conversation theme, both windows (#2098) — the theme
   * filter of the Voice of Customer section. The same gate and refusals as
   * `voiceOfCustomerQuerySchema.themeCode`; the answer carries `themeFilter`.
   */
  themeCode: voiceThemeFilterCodeSchema.optional(),
  /**
   * The rule Р3 marks of the codes (#2552) — the Voice of Customer Sales tab
   * asks. Only behind the Voice of Customer gate (tier and flag); otherwise
   * the answer carries no `mark`, and the stream itself stays open to every
   * plan.
   */
  marks: z.enum(['true', 'false']).optional(),
});

/** A client's quote of the objection, with the call it links to. */
export const objectionStreamQuoteSchema = z.object({
  recordId: z.string(),
  /** The call's time (ISO): the Voice section narrows quotes to a series bucket by it. */
  createdAt: z.string(),
  quote: z.string(),
  handled: z.boolean(),
  /** Handling score 0-100; null when the analysis carried none. */
  score: z.number().nullable(),
  agentName: z.string().nullable(),
  /**
   * Best answers only: the call's "ASR label → side" map from the analysis
   * (machine labels, no names), so the excerpt tells the manager from the
   * customer the way the record's transcript does.
   */
  speakerRoles: z.record(z.string(), z.string()).optional(),
});

/** An operator who handles the code worst - named only past 3 calls with it. */
export const objectionStreamAgentSchema = z.object({
  agentId: z.string(),
  agentName: z.string().nullable(),
  calls: z.number().int().positive(),
  unhandledCalls: z.number().int().nonnegative(),
});

/**
 * One objection code over the window. The unit is the call: a call with two
 * price objections counts once. `code` is a plain string for the same reason
 * as in `teamObjectionSchema` - a newer code set must not reject the response.
 */
export const objectionStreamCodeSchema = z.object({
  code: z.string(),
  calls: z.number().int().positive(),
  /** Calls where at least one objection of the code was not handled. */
  unhandledCalls: z.number().int().nonnegative(),
  /** Mean handling score over the code's objections; null when none was scored. */
  avgScore: z.number().nullable(),
  /** Calls with the code in the previous window of the same length. */
  prevCalls: z.number().int().nonnegative().nullable(),
  /**
   * The rule Р3 mark of the code (#2552): «up», «down» or «new» — N is the
   * calls with a recognized objection (the share's denominator), in the fact
   * history too (#2698): «new» only when no covered earlier period held the
   * code, and a period with an objection no reason took confirms nothing.
   * Never «gone»: only codes heard in the window are listed. No field — no
   * mark (not asked, the Voice of Customer gate closed, a capped scan, the
   * share moved too little).
   */
  mark: voiceMarkSchema.optional(),
  /** Up to 5 live wordings, most calls first. */
  wordings: z.array(z.string()),
  /** Up to 8 client quotes from different calls, a new operator first. */
  quotes: z.array(objectionStreamQuoteSchema),
  /** Up to 3 calls with the best handling of the code. */
  bestHandled: z.array(objectionStreamQuoteSchema),
  weakestAgents: z.array(objectionStreamAgentSchema),
  /**
   * Calls per deal stage at the time of the call; `unmapped` - a stage with
   * no phase; `unknown` - no CRM stage.
   */
  byStage: z.record(z.string(), z.number().int().nonnegative()),
  /**
   * Of `byStage`, the calls whose phase was guessed from the stage name
   * (#1206) — the screen marks them. Optional with a default: an older API
   * has no such field, and a missing one must not fail the whole response.
   */
  byStageGuessed: z.record(z.string(), z.number().int().nonnegative()).default({}),
  /** Calls per deal movement after the call. */
  byProgression: z.object({
    advanced: z.number().int().nonnegative(),
    held: z.number().int().nonnegative(),
    regressed: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
  }),
});

export const objectionStreamResponseSchema = z.object({
  codes: z.array(objectionStreamCodeSchema),
  /** Calls of the window with at least one objection - "in K of N calls with objections". */
  callsWithObjections: z.number().int().nonnegative(),
  prevCallsWithObjections: z.number().int().nonnegative().nullable(),
  /**
   * Calls with at least one RECOGNIZED objection, in this and the previous
   * window — the denominator of the shift against the previous window, so a
   * change in how much history could be recognized is not read as the market.
   */
  recognizedCalls: z.number().int().nonnegative(),
  prevRecognizedCalls: z.number().int().nonnegative().nullable(),
  /** The previous window held an unrecognized objection — "new" cannot be claimed. */
  prevHasUnrecognized: z.boolean().nullable(),
  /** Every analysed call of the window - "N calls in the window". */
  callsInWindow: z.number().int().nonnegative(),
  unmappedShare: z.number().min(0).max(1),
  otherShare: z.number().min(0).max(1),
  /** The current window held more calls than the scan cap; its counts are a lower bound. */
  truncated: z.boolean(),
  /** The previous window hit the cap; only the comparison is a lower bound. */
  prevTruncated: z.boolean(),
  periodStart: z.string(),
  periodEnd: z.string(),
  /** The previous window as queried; it ends just before `periodStart`. */
  prevPeriodStart: z.string(),
  prevPeriodEnd: z.string(),
  /**
   * The theme filter's echo — only with `themeCode` (#2098). `calls` /
   * `windowCalls` are the analysed calls of the window with and without the
   * theme (`callsInWindow`'s set); `prevComparable: false` — the previous
   * window was not read, its counts are not a comparison.
   */
  themeFilter: voiceThemeFilterEchoSchema.optional(),
  /** The stream was served from the demo rows alone (a closed section, #2816). */
  demoOnly: lockedDemoOnlySchema,
});

// ============================================================================
// Type exports
// ============================================================================

export type AgentPerformanceQuery = z.infer<typeof agentPerformanceQuerySchema>;
export type AgentByIdPerformanceQuery = z.infer<typeof agentByIdPerformanceQuerySchema>;
export type AgentMePerformanceQuery = z.infer<typeof agentMePerformanceQuerySchema>;
export type TeamLeaderboardQuery = z.infer<typeof teamLeaderboardQuerySchema>;
export type TeamLeaderboardOrder = z.infer<typeof teamLeaderboardOrderSchema>;
export type TeamLeaderboardScope = z.infer<typeof teamLeaderboardScopeSchema>;
export type QualityMetric = z.infer<typeof qualityMetricSchema>;
export type FocusArea = z.infer<typeof focusAreaSchema>;
export type AgentRecord = z.infer<typeof agentRecordSchema>;
export type QualityScore = z.infer<typeof qualityScoreSchema>;
export type Ranking = z.infer<typeof rankingSchema>;
export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;
export type AgentPerformanceResponse = z.infer<typeof agentPerformanceResponseSchema>;
export type TeamLeaderboardResponse = z.infer<typeof teamLeaderboardResponseSchema>;
export type ScoreDay = z.infer<typeof scoreDaySchema>;
export type CallStats = z.infer<typeof callStatsSchema>;
export type WeeklyScore = z.infer<typeof weeklyScoreSchema>;
export type CoachingTip = z.infer<typeof coachingTipSchema>;
export type CoachingMetricScore = z.infer<typeof coachingMetricScoreSchema>;
export type CoachingImprovementItem = z.infer<typeof coachingImprovementItemSchema>;
export type CoachingSession = z.infer<typeof coachingSessionSchema>;
export type CreateCoachingSessionRequest = z.infer<
  typeof createCoachingSessionRequestSchema
>;
export type CompleteCoachingSessionRequest = z.infer<
  typeof completeCoachingSessionRequestSchema
>;
export type SupervisorNote = z.infer<typeof supervisorNoteSchema>;
export type SupervisorNoteResponse = z.infer<typeof supervisorNoteResponseSchema>;
export type SupervisorNotesResponse = z.infer<typeof supervisorNotesResponseSchema>;
export type MyCoachingSessionsQuery = z.infer<typeof myCoachingSessionsQuerySchema>;
export type MyCoachingSession = z.infer<typeof myCoachingSessionSchema>;
export type MyCoachingSessionsResponse = z.infer<typeof myCoachingSessionsResponseSchema>;
export type AICoachingInsights = z.infer<typeof aiCoachingInsightsSchema>;
export type WeeklyCoachingPlan = z.infer<typeof weeklyCoachingPlanSchema>;
export type CoachingInsightsResponse = z.infer<typeof coachingInsightsResponseSchema>;
export type WeeklyCoachingPlanResponse = z.infer<typeof weeklyCoachingPlanResponseSchema>;
export type MeCompetitionQuery = z.infer<typeof meCompetitionQuerySchema>;
export type CompetitionEntry = z.infer<typeof competitionEntrySchema>;
export type MeCompetitionResponse = z.infer<typeof meCompetitionResponseSchema>;
export type ScriptAdherenceQuery = z.infer<typeof scriptAdherenceQuerySchema>;
export type AgentScriptAdherence = z.infer<typeof agentScriptAdherenceSchema>;
export type SkippedScriptStep = z.infer<typeof skippedScriptStepSchema>;
export type ScriptAdherenceResponse = z.infer<typeof scriptAdherenceResponseSchema>;
export type ObjectionsQuery = z.infer<typeof objectionsQuerySchema>;
export type TeamObjection = z.infer<typeof teamObjectionSchema>;
export type ObjectionsResponse = z.infer<typeof objectionsResponseSchema>;
export type ObjectionStreamQuery = z.infer<typeof objectionStreamQuerySchema>;
export type ObjectionStreamQuote = z.infer<typeof objectionStreamQuoteSchema>;
export type ObjectionStreamAgent = z.infer<typeof objectionStreamAgentSchema>;
export type ObjectionStreamCode = z.infer<typeof objectionStreamCodeSchema>;
export type ObjectionStreamResponse = z.infer<typeof objectionStreamResponseSchema>;
